// Base API Service - Common functionality for all API services
import environment from "../config/environment";

/**
 * Base API Service class that provides common HTTP request functionality
 * All API services should extend or use this base class
 */
class BaseApiService {
  constructor(baseURL = null, timeout = null) {
    this.baseURL = baseURL || environment.PHP_API_URL;
    this.timeout = timeout || environment.API_TIMEOUT;
  }

  /**
   * Generic API call method
   * @param {string} endpoint - API endpoint (relative or absolute URL)
   * @param {Object} options - Request options (method, body, headers, etc.)
   * @returns {Promise<Object>} - Parsed JSON response
   */
  async makeRequest(endpoint, options = {}) {
    const isAbsolute = /^https?:\/\//i.test(endpoint);
    // Remove leading slash from endpoint if baseURL already ends with slash
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    // Ensure baseURL doesn't have trailing slash when constructing URL
    const baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    const url = isAbsolute ? endpoint : `${baseURL}/${cleanEndpoint}`;

    const defaultOptions = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      timeout: this.timeout,
    };

    const requestOptions = { ...defaultOptions, ...options };

    // Log the full URL with parameters
    console.log("[BaseApiService] makeRequest:");
    console.log("  Method:", requestOptions.method);
    console.log("  Full URL:", url);
    if (requestOptions.body) {
      console.log("  Body:", requestOptions.body);
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...requestOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Log the response
      console.log("[BaseApiService] Response:", {
        status: response.status,
        statusText: response.statusText,
        data: data
      });
      
      return data;
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Request timeout");
      }
      throw error;
    }
  }

  /**
   * Handle API response and return standardized format
   * @param {Object} response - API response object
   * @param {string} successMessage - Success message
   * @param {string} errorMessage - Error message
   * @returns {Object} - Standardized response { success, data, error, message }
   */
  handleResponse(response, successMessage = "Request successful", errorMessage = "Request failed") {
    if (response.api_status === "OK") {
      return {
        success: true,
        data: response.data || response,
        message: successMessage,
      };
    } else {
      return {
        success: false,
        error: response.api_remark || errorMessage,
        message: response.api_remark || errorMessage,
      };
    }
  }

  /**
   * Handle errors and return standardized error format
   * @param {Error} error - Error object
   * @param {string} customMessage - Custom error message
   * @returns {Object} - Standardized error response { success, error, message }
   */
  handleError(error, customMessage = "Network error or server unavailable") {
    return {
      success: false,
      error: error.message,
      message: customMessage,
    };
  }

  /**
   * Build URL with query parameters
   * @param {string} endpoint - Base endpoint
   * @param {Object} params - Query parameters object
   * @returns {string} - URL with query string
   */
  buildUrlWithParams(endpoint, params = {}) {
    const url = new URL(endpoint, this.baseURL);
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== "") {
        url.searchParams.append(key, params[key]);
      }
    });
    return url.toString().replace(this.baseURL, "");
  }

  /**
   * Create URLSearchParams from object
   * @param {Object} params - Parameters object
   * @returns {URLSearchParams} - URLSearchParams instance
   */
  createSearchParams(params = {}) {
    const searchParams = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== "") {
        searchParams.append(key, params[key]);
      }
    });
    return searchParams;
  }
}

export default BaseApiService;
